// frontend/src/MediaAssets/MediaAssetViewer.tsx
import React, { useMemo, useState } from "react";
import { ActionIcon, Modal } from "@mantine/core";
import type { IBlobMedia } from "@multiconsult-group/multiplacer-core";
import { ZMediaAssetKeyKnown, type IMediaAssetKeyKnown } from "@multiconsult-group/multiplacer-core";
import { useAppSelector } from "../app/hooks";
import { BlobMediaPreview } from "../Releasenotes/BlobMedia";
import { selectHttpMediaAssetsCached } from "./mediaSelectors";
import { useGetMultiplacerMediaAssetsQuery } from "../../openapi";
import { HiVideoCamera } from "react-icons/hi2";

export type IMediaAssetViewerMode = "ICON" | "EXPANDED";

type Props = {
  assetKey: IMediaAssetKeyKnown; // typed against enum
  mode: IMediaAssetViewerMode;
  iconSize?: number;
  modalTitle?: string;
};

export const MediaAssetViewer: React.FC<Props> = ({
  assetKey,
  mode,
  iconSize = 16,
  modalTitle,
}) => {
  useGetMultiplacerMediaAssetsQuery();
  const assets = useAppSelector(selectHttpMediaAssetsCached);

  const asset = useMemo(() => {
    // assetKey is already strongly typed, but this keeps it robust if DB has weird data
    const keyOk = ZMediaAssetKeyKnown.safeParse(assetKey).success;
    if (!keyOk) return undefined;
    return assets.find((a) => a.key === assetKey);
  }, [assets, assetKey]);

  const [opened, setOpened] = useState(false);

  const previewMedia: IBlobMedia | null = useMemo(() => {
    if (!asset) return null;

    if (asset.kind === "VIDEO") {
      return {
        kind: "video",
        blobPath: asset.blobPath,
        contentType: asset.mimeType ?? "video/mp4",
      };
    }

    // FILE: only preview if it's an image
    if (asset.mimeType?.startsWith("image/")) {
      return {
        kind: "image",
        blobPath: asset.blobPath,
        contentType: asset.mimeType,
      };
    }

    return null;
  }, [asset]);

  // ✅ If key not found in DB => render nothing
  if (!asset || !previewMedia) return null;

  if (mode === "EXPANDED") {
    return <BlobMediaPreview media={previewMedia} target="media-assets" />;
  }

  // ICON mode
  return (
    <>
      <ActionIcon
        variant="transparent"
        color="green"
        size={iconSize}
        radius={0}
        onClick={() => setOpened(true)}
        aria-label="Vis media"
      >
        <HiVideoCamera size={iconSize} />
      </ActionIcon>

      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        title={modalTitle ?? asset.title}
        size="xl"
        centered
      >
        <BlobMediaPreview media={previewMedia} target="media-assets" />
      </Modal>
    </>
  );
};
